import { useState } from 'react';
import { Link } from 'react-router-dom';
import FourPanelManga from '../../components/feature/FourPanelManga';
import AnimeDetailModal from './components/AnimeDetailModal';
import MangaDetailModal from './components/MangaDetailModal';

export default function Entertainment() {
  const [activeCategory, setActiveCategory] = useState('podcast');
  const [playingEpisode, setPlayingEpisode] = useState<number | null>(null);
  const [currentAudio, setCurrentAudio] = useState<HTMLAudioElement | null>(null);
  const [selectedAnime, setSelectedAnime] = useState<{
    title: string;
    description: string;
    thumbnail: string;
    duration: string;
    category: string;
    views: string;
    rating: number;
    tags: string[];
    watchUrl: string;
  } | null>(null);

  const [selectedManga, setSelectedManga] = useState<{
    title: string;
    description: string;
    thumbnail: string;
    episodes: number;
    latestEpisode: number;
    status: string;
    genre: string;
    rating: number;
    reads: string;
    tags: string[];
    updatedAt: string;
    author: string;
  } | null>(null);

  const handlePlayAudio = (episodeNumber: number, audioUrl: string) => {
    if (currentAudio) {
      currentAudio.pause();
      currentAudio.currentTime = 0;
    }
    if (playingEpisode === episodeNumber) {
      setCurrentAudio(null);
      setPlayingEpisode(null);
      return;
    }
    const audio = new Audio(audioUrl);
    audio.play();
    setCurrentAudio(audio);
    setPlayingEpisode(episodeNumber);
    audio.onended = () => {
      setCurrentAudio(null);
      setPlayingEpisode(null);
    };
  };

  const podcastEpisodes = [
    {
      id: 1,
      episode: 'Episode 01',
      title: '2026年診療報酬改定（短冊）',
      description: '1月23日通達の2026年診療報酬改定短冊についてのポイントを解説します。',
      date: '2025.01.24',
      duration: '19分',
      isNew: true,
      gradient: 'from-amber-400 via-orange-400 to-red-400',
      youtubeUrl: 'https://youtu.be/pq5mrty1YUU',
      audioUrl: '/audio/podcast-episode1.mp3',
    },
    {
      id: 2,
      episode: 'Episode 02',
      title: 'チームビルディングの極意 〜職員の力を最大化する〜',
      description: '薬局スタッフのモチベーションを高め、チーム全体のパフォーマンスを向上させる方法を紹介。',
      date: '2026.04.05',
      duration: '6-7分',
      isNew: false,
      gradient: 'from-teal-400 via-cyan-400 to-blue-400',
      youtubeUrl: 'https://www.youtube.com/watch?v=YlY1CC8ab2E',
      audioUrl: '/audio/podcast-episode2.mp3',
    },
    {
      id: 3,
      episode: 'Episode 03',
      title: 'キャリア選択の納得感 〜自分らしい薬剤師像を描く〜',
      description: '薬剤師としてのキャリアパスを考える上で大切な視点と、納得のいく選択をするためのヒント。',
      date: '2025.01.01',
      duration: '35分',
      isNew: false,
      gradient: 'from-violet-400 via-purple-400 to-fuchsia-400',
      youtubeUrl: '',
      audioUrl: '/audio/podcast-episode3.mp3',
    },
    {
      id: 4,
      episode: 'Episode 04',
      title: '薬局DXの最前線 〜電子処方箋とオンライン服薬指導〜',
      description: '急速に進む薬局のデジタル化。電子処方箋の普及状況とオンライン服薬指導の実践的な取り組みを解説。',
      date: '2024.12.20',
      duration: '42分',
      isNew: false,
      gradient: 'from-emerald-400 via-teal-400 to-cyan-500',
      youtubeUrl: '',
      audioUrl: '/audio/podcast-episode4.mp3',
    },
    {
      id: 5,
      episode: 'Episode 05',
      title: '在宅医療における薬剤師の役割',
      description: '高齢化社会が進む中、在宅医療での薬剤師の重要性と多職種連携の実践例を紹介します。',
      date: '2024.12.10',
      duration: '31分',
      isNew: false,
      gradient: 'from-rose-400 via-pink-400 to-red-400',
      youtubeUrl: '',
      audioUrl: '/audio/podcast-episode5.mp3',
    },
    {
      id: 6,
      episode: 'Episode 06',
      title: 'OTC医薬品の選び方と薬剤師の役割',
      description: 'セルフメディケーションが注目される中、薬剤師がOTC医薬品の選択をどうサポートするかを解説。',
      date: '2024.12.01',
      duration: '26分',
      isNew: false,
      gradient: 'from-orange-400 via-amber-400 to-yellow-400',
      youtubeUrl: '',
      audioUrl: '/audio/podcast-episode6.mp3',
    },
  ];

  const animeContent = [
    {
      title: '薬剤師のお仕事アニメ',
      description: '薬剤師の日常業務をアニメで楽しく学ぼう。調剤から服薬指導まで、現場のリアルをわかりやすく紹介します。',
      thumbnail: 'https://public.readdy.ai/ai/img_res/f01d923820402f9f5bb720bb2409fb5f.jpg',
      duration: '5分',
      category: 'educational',
      views: '12,400',
      rating: 4.8,
      tags: ['調剤', '服薬指導', '入門'],
      watchUrl: '#',
    },
    {
      title: '薬の冒険物語',
      description: '薬が体の中でどう働くかをアニメで解説。薬物動態をキャラクターで楽しく学べる人気シリーズ。',
      thumbnail: 'https://readdy.ai/api/search-image?query=Anime%20style%20medicine%20adventure%20story%2C%20cartoon%20pills%20and%20capsules%20as%20cute%20characters%2C%20colorful%20journey%20through%20human%20body%20organs%2C%20educational%20animation%20style%2C%20bright%20engaging%20visuals%2C%20soft%20pastel%20background&width=400&height=240&seq=anime-medicine-adventure&orientation=landscape',
      duration: '8分',
      category: 'educational',
      views: '9,800',
      rating: 4.7,
      tags: ['薬物動態', '薬理学', '基礎知識'],
      watchUrl: '#',
    },
    {
      title: '薬局の一日',
      description: '薬局での一日の流れをアニメで紹介。患者対応から在庫管理まで、薬局業務の全体像がわかります。',
      thumbnail: 'https://readdy.ai/api/search-image?query=Anime%20style%20pharmacy%20daily%20routine%2C%20cartoon%20pharmacist%20characters%20serving%20patients%2C%20colorful%20pharmacy%20interior%20with%20shelves%20of%20medicine%2C%20friendly%20customer%20service%20scenes%2C%20educational%20animation%2C%20warm%20lighting&width=400&height=240&seq=anime-pharmacy-day&orientation=landscape',
      duration: '6分',
      category: 'daily-life',
      views: '7,200',
      rating: 4.6,
      tags: ['日常業務', '患者対応', '薬局運営'],
      watchUrl: '#',
    },
    {
      title: 'お薬の正しい飲み方',
      description: '薬の正しい服用方法をアニメで学習。飲み合わせや食事との関係など、患者さんへの指導にも役立つ内容。',
      thumbnail: 'https://readdy.ai/api/search-image?query=Anime%20style%20medication%20instruction%20guide%2C%20cartoon%20characters%20demonstrating%20proper%20medicine%20taking%20methods%2C%20educational%20animation%20with%20clear%20visual%20instructions%2C%20colorful%20informative%2C%20clean%20white%20background%20with%20pastel%20accents&width=400&height=240&seq=anime-medication-guide&orientation=landscape',
      duration: '4分',
      category: 'educational',
      views: '15,600',
      rating: 4.9,
      tags: ['服薬指導', '飲み合わせ', '患者教育'],
      watchUrl: '#',
    },
    {
      title: '在宅医療と薬剤師',
      description: '高齢化社会における在宅医療での薬剤師の役割をアニメで解説。多職種連携の重要性も学べます。',
      thumbnail: 'https://readdy.ai/api/search-image?query=Anime%20style%20home%20medical%20care%20scene%2C%20cartoon%20pharmacist%20visiting%20elderly%20patient%20at%20home%2C%20warm%20cozy%20home%20interior%2C%20friendly%20healthcare%20team%20collaboration%2C%20soft%20pastel%20colors%2C%20heartwarming%20atmosphere&width=400&height=240&seq=anime-home-care&orientation=landscape',
      duration: '10分',
      category: 'advanced',
      views: '6,300',
      rating: 4.7,
      tags: ['在宅医療', '多職種連携', '高齢者ケア'],
      watchUrl: '#',
    },
    {
      title: 'ジェネリック医薬品の真実',
      description: 'ジェネリック医薬品の仕組みと安全性をわかりやすくアニメで解説。患者さんへの説明にも活用できます。',
      thumbnail: 'https://readdy.ai/api/search-image?query=Anime%20style%20generic%20medicine%20explanation%2C%20cartoon%20characters%20comparing%20brand%20name%20and%20generic%20pills%2C%20colorful%20educational%20animation%2C%20laboratory%20and%20pharmacy%20setting%2C%20clear%20visual%20comparison%2C%20bright%20cheerful%20colors&width=400&height=240&seq=anime-generic-medicine&orientation=landscape',
      duration: '7分',
      category: 'educational',
      views: '11,200',
      rating: 4.8,
      tags: ['ジェネリック', '後発品', '患者説明'],
      watchUrl: '#',
    },
    {
      title: '漢方薬入門アニメ',
      description: '漢方薬の基礎知識をアニメで楽しく学ぼう。証の考え方から代表的な処方まで、初心者にもわかりやすく解説。',
      thumbnail: 'https://readdy.ai/api/search-image?query=Anime%20style%20traditional%20Japanese%20herbal%20medicine%20kampo%2C%20cartoon%20characters%20with%20herbs%20and%20natural%20ingredients%2C%20colorful%20traditional%20medicine%20shop%20setting%2C%20educational%20animation%2C%20warm%20earthy%20tones%20with%20pastel%20accents&width=400&height=240&seq=anime-kampo&orientation=landscape',
      duration: '12分',
      category: 'advanced',
      views: '5,800',
      rating: 4.6,
      tags: ['漢方', '東洋医学', '証'],
      watchUrl: '#',
    },
    {
      title: '薬局DX最前線',
      description: '電子処方箋やオンライン服薬指導など、薬局のデジタル化をアニメで解説。未来の薬局の姿が見えてきます。',
      thumbnail: 'https://readdy.ai/api/search-image?query=Anime%20style%20digital%20pharmacy%20future%2C%20cartoon%20pharmacist%20using%20tablet%20and%20computer%2C%20electronic%20prescription%20system%2C%20modern%20high-tech%20pharmacy%20interior%2C%20futuristic%20but%20friendly%20atmosphere%2C%20cool%20blue%20and%20teal%20color%20palette&width=400&height=240&seq=anime-dx-pharmacy&orientation=landscape',
      duration: '9分',
      category: 'advanced',
      views: '8,900',
      rating: 4.9,
      tags: ['DX', '電子処方箋', 'オンライン服薬指導'],
      watchUrl: '#',
    },
  ];

  const comics = [
    {
      title: '薬剤師まんが日記',
      description: '現役薬剤師の日常をコミカルかつリアルに描いた4コマ漫画。薬局あるあるが満載で、笑いながら学べる人気シリーズ。',
      thumbnail: 'https://readdy.ai/api/search-image?query=Manga%20style%20comic%20strip%20pharmacist%20daily%20life%2C%20cute%20chibi%20pharmacist%20character%20in%20white%20coat%2C%204-panel%20comic%20layout%2C%20colorful%20pastel%20illustration%2C%20cheerful%20pharmacy%20setting%2C%20Japanese%20manga%20art%20style%2C%20warm%20orange%20and%20amber%20tones&width=400&height=520&seq=manga-diary-cover&orientation=portrait',
      episodes: 50,
      latestEpisode: 50,
      status: '連載中',
      genre: '4コマ',
      rating: 4.8,
      reads: '28,400',
      tags: ['日常', 'コメディ', '薬局あるある', '4コマ'],
      updatedAt: '2025.01.20',
      author: 'ビースト先生',
    },
    {
      title: '薬の歴史物語',
      description: '古代エジプトから現代まで、薬の発見と発展の歴史をマンガで解説。感動的な実話が満載の全12話完結作品。',
      thumbnail: 'https://readdy.ai/api/search-image?query=Manga%20style%20historical%20medicine%20story%2C%20ancient%20and%20modern%20medicine%20illustration%2C%20dramatic%20manga%20art%2C%20historical%20characters%20discovering%20medicine%2C%20epic%20adventure%20style%2C%20rich%20detailed%20background%2C%20sepia%20and%20gold%20color%20palette&width=400&height=520&seq=manga-history-cover&orientation=portrait',
      episodes: 12,
      latestEpisode: 12,
      status: '完結',
      genre: '歴史',
      rating: 4.7,
      reads: '15,200',
      tags: ['歴史', '薬学', '教育', 'ノンフィクション'],
      updatedAt: '2024.10.15',
      author: '薬史郎',
    },
    {
      title: '薬局あるある',
      description: '薬局で働く人なら絶対共感できるあるあるネタを集めたコメディマンガ。クスッと笑えるエピソードが30話収録。',
      thumbnail: 'https://readdy.ai/api/search-image?query=Manga%20style%20pharmacy%20comedy%20comic%2C%20funny%20pharmacist%20characters%20in%20various%20situations%2C%20expressive%20manga%20faces%2C%20colorful%20comic%20panels%2C%20humorous%20pharmacy%20scenes%2C%20bright%20cheerful%20colors%2C%20Japanese%20manga%20style&width=400&height=520&seq=manga-arua-cover&orientation=portrait',
      episodes: 30,
      latestEpisode: 30,
      status: '連載中',
      genre: 'コメディ',
      rating: 4.6,
      reads: '19,800',
      tags: ['コメディ', 'あるある', '薬剤師', '共感'],
      updatedAt: '2025.01.10',
      author: 'あるある薬剤師',
    },
    {
      title: '薬剤師キャリア図鑑',
      description: '調剤薬局・病院・ドラッグストア・製薬会社など、薬剤師のさまざまなキャリアパスをマンガで紹介する実用的な一冊。',
      thumbnail: 'https://readdy.ai/api/search-image?query=Manga%20style%20career%20guide%20pharmacist%2C%20multiple%20pharmacist%20characters%20in%20different%20work%20settings%2C%20professional%20manga%20illustration%2C%20career%20path%20diagram%2C%20colorful%20character%20designs%2C%20motivational%20atmosphere%2C%20clean%20modern%20style&width=400&height=520&seq=manga-career-cover&orientation=portrait',
      episodes: 10,
      latestEpisode: 8,
      status: '連載中',
      genre: 'キャリア',
      rating: 4.9,
      reads: '22,100',
      tags: ['キャリア', '転職', '薬剤師', '就活'],
      updatedAt: '2025.01.18',
      author: 'キャリア薬剤師',
    },
    {
      title: '薬と食べ物の不思議な関係',
      description: 'グレープフルーツ・納豆・牛乳など、身近な食べ物と薬の意外な相互作用をマンガで解説。服薬指導にも役立つ実用作品。',
      thumbnail: 'https://readdy.ai/api/search-image?query=Manga%20style%20food%20and%20medicine%20interaction%2C%20cute%20food%20characters%20and%20medicine%20characters%20interacting%2C%20educational%20manga%20illustration%2C%20colorful%20food%20items%20and%20pills%2C%20chemistry%20reaction%20visual%2C%20bright%20informative%20style&width=400&height=520&seq=manga-food-cover&orientation=portrait',
      episodes: 20,
      latestEpisode: 20,
      status: '完結',
      genre: '教育',
      rating: 4.8,
      reads: '31,500',
      tags: ['食べ合わせ', '服薬指導', '相互作用', '実用'],
      updatedAt: '2024.12.01',
      author: '相互作用先生',
    },
    {
      title: '薬局経営マンガ塾',
      description: '独立開業を目指す薬剤師や薬局経営者のための実践的なマンガ。経営・マネジメント・DX推進まで幅広く解説。',
      thumbnail: 'https://readdy.ai/api/search-image?query=Manga%20style%20pharmacy%20business%20management%2C%20professional%20pharmacist%20manager%20character%2C%20business%20strategy%20manga%20illustration%2C%20modern%20pharmacy%20interior%2C%20leadership%20and%20teamwork%20scene%2C%20clean%20professional%20style%2C%20amber%20and%20teal%20colors&width=400&height=520&seq=manga-biz-cover&orientation=portrait',
      episodes: 18,
      latestEpisode: 15,
      status: '連載中',
      genre: '経営',
      rating: 4.7,
      reads: '12,300',
      tags: ['経営', '開業', 'マネジメント', 'DX'],
      updatedAt: '2025.01.22',
      author: '経営薬剤師',
    },
  ];

  return (
    <div className="min-h-screen bg-white">
      <AnimeDetailModal anime={selectedAnime} onClose={() => setSelectedAnime(null)} />
      <MangaDetailModal manga={selectedManga} onClose={() => setSelectedManga(null)} />
      {/* Header */}
      <header className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <Link to="/" className="flex items-center">
              <div className="w-10 h-10 mr-3 relative">
                <div className="absolute inset-0 bg-gradient-to-br from-teal-500 to-emerald-500 rounded-lg transform rotate-12 shadow-lg"></div>
                <div className="absolute inset-0 bg-gradient-to-br from-teal-400 to-emerald-400 rounded-lg flex items-center justify-center">
                  <i className="ri-heart-pulse-fill text-white text-lg"></i>
                </div>
              </div>
              <div className="text-xl font-bold text-teal-600" style={{ fontFamily: '"Pacifico", serif' }}>
                みんなの薬局～Pharmacare～
              </div>
            </Link>
            <nav className="hidden md:flex space-x-8">
              <Link to="/services" className="text-gray-700 hover:text-teal-600 font-medium whitespace-nowrap cursor-pointer">サービス</Link>
              <Link to="/health-support" className="text-gray-700 hover:text-teal-600 font-medium whitespace-nowrap cursor-pointer">健康サポート</Link>
              <Link to="/pharmacist-resources" className="text-gray-700 hover:text-teal-600 font-medium whitespace-nowrap cursor-pointer">薬剤師向け</Link>
              <Link to="/information" className="text-gray-700 hover:text-teal-600 font-medium whitespace-nowrap cursor-pointer">情報提供</Link>
            </nav>
            <button className="md:hidden p-2">
              <i className="ri-menu-line text-xl"></i>
            </button>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-amber-50 via-orange-50 to-yellow-50 py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-amber-400 to-orange-500 rounded-full mb-6 shadow-lg">
            <i className="ri-mic-line text-3xl text-white"></i>
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            PharmaBeast <span className="text-amber-500">Podcast</span>
          </h1>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            薬剤師のための学びの場。現場で役立つ知識や考え方を音声でお届けします。<br />
            通勤中や休憩時間に、気軽に学べるコンテンツです。
          </p>
        </div>
      </section>

      {/* Category Tabs */}
      <section className="py-6 bg-white border-b sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-center">
            <div className="flex bg-gray-100 rounded-full p-1">
              <button
                onClick={() => setActiveCategory('podcast')}
                className={`px-6 py-2 rounded-full font-medium transition-all whitespace-nowrap cursor-pointer text-sm ${
                  activeCategory === 'podcast' ? 'bg-amber-500 text-white shadow' : 'text-gray-600 hover:text-amber-600'
                }`}
              >
                <i className="ri-mic-line mr-1"></i>ポッドキャスト
              </button>
              <button
                onClick={() => setActiveCategory('anime')}
                className={`px-6 py-2 rounded-full font-medium transition-all whitespace-nowrap cursor-pointer text-sm ${
                  activeCategory === 'anime' ? 'bg-amber-500 text-white shadow' : 'text-gray-600 hover:text-amber-600'
                }`}
              >
                <i className="ri-tv-line mr-1"></i>アニメ
              </button>
              <button
                onClick={() => setActiveCategory('comics')}
                className={`px-6 py-2 rounded-full font-medium transition-all whitespace-nowrap cursor-pointer text-sm ${
                  activeCategory === 'comics' ? 'bg-amber-500 text-white shadow' : 'text-gray-600 hover:text-amber-600'
                }`}
              >
                <i className="ri-book-line mr-1"></i>マンガ
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Content Sections */}
      <section className="py-16 bg-gradient-to-br from-amber-50 via-orange-50 to-yellow-50 min-h-[60vh]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

          {/* ===== PODCAST TAB ===== */}
          {activeCategory === 'podcast' && (
            <div>
              {/* エピソードグリッド */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
                {podcastEpisodes.map((ep) => (
                  <div
                    key={ep.id}
                    className="bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 overflow-hidden border border-amber-100 group"
                  >
                    {/* サムネイル */}
                    {ep.youtubeUrl ? (
                      <a
                        href={ep.youtubeUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={`block relative h-48 bg-gradient-to-br ${ep.gradient} flex items-center justify-center`}
                      >
                        <div className="absolute inset-0 bg-black/10"></div>
                        <div className="relative z-10 text-center">
                          <div className="w-20 h-20 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center mx-auto mb-3 group-hover:scale-110 transition-transform">
                            <i className="ri-play-fill text-4xl text-white"></i>
                          </div>
                          <span className="text-white/90 text-sm font-medium">{ep.episode}</span>
                        </div>
                      </a>
                    ) : (
                      <div className={`relative h-48 bg-gradient-to-br ${ep.gradient} flex items-center justify-center`}>
                        <div className="absolute inset-0 bg-black/10"></div>
                        <div className="relative z-10 text-center">
                          <div className="w-20 h-20 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center mx-auto mb-3 group-hover:scale-110 transition-transform">
                            <i className="ri-play-fill text-4xl text-white"></i>
                          </div>
                          <span className="text-white/90 text-sm font-medium">{ep.episode}</span>
                        </div>
                      </div>
                    )}

                    {/* カード本文 */}
                    <div className="p-6">
                      <div className="flex items-center gap-3 text-sm text-gray-500 mb-3">
                        {ep.isNew && (
                          <span className="bg-amber-100 text-amber-700 px-2 py-1 rounded-full text-xs font-medium">NEW</span>
                        )}
                        <span>{ep.date}</span>
                        <span>・</span>
                        <span>{ep.duration}</span>
                      </div>
                      <h3 className="text-base font-bold text-gray-900 mb-2 group-hover:text-amber-600 transition-colors leading-snug">
                        {ep.title}
                      </h3>
                      <p className="text-sm text-gray-600 leading-relaxed mb-4">
                        {ep.description}
                      </p>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <img
                            src="https://readdy.ai/api/search-image?query=Professional%20pharmacist%20portrait%20headshot%2C%20friendly%20smile%2C%20white%20coat%2C%20clean%20background%2C%20professional%20healthcare%20worker%2C%20warm%20lighting&width=40&height=40&seq=podcast-host-ent&orientation=squarish"
                            alt="ホスト"
                            className="w-8 h-8 rounded-full object-cover"
                          />
                          <span className="text-sm text-gray-600">ビースト君</span>
                        </div>
                        {ep.youtubeUrl ? (
                          <a
                            href={ep.youtubeUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center gap-2 text-amber-600 hover:text-amber-700 font-medium text-sm cursor-pointer whitespace-nowrap"
                          >
                            <i className="ri-headphone-line"></i>
                            聴く
                          </a>
                        ) : (
                          <button
                            onClick={() => handlePlayAudio(ep.id, ep.audioUrl)}
                            className="flex items-center gap-2 text-amber-600 hover:text-amber-700 font-medium text-sm cursor-pointer whitespace-nowrap"
                          >
                            <i className={playingEpisode === ep.id ? 'ri-pause-circle-line' : 'ri-headphone-line'}></i>
                            {playingEpisode === ep.id ? '停止' : '聴く'}
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* 配信プラットフォーム */}
              <div className="bg-white rounded-2xl shadow-lg p-8 border border-amber-100">
                <div className="text-center mb-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-2">配信プラットフォーム</h3>
                  <p className="text-gray-600 text-sm">お好みのプラットフォームでお聴きいただけます</p>
                </div>
                <div className="flex flex-wrap justify-center gap-4">
                  <a href="#" className="flex items-center gap-2 bg-gray-100 hover:bg-gray-200 px-6 py-3 rounded-full transition-colors cursor-pointer whitespace-nowrap">
                    <i className="ri-youtube-line text-xl text-red-600"></i>
                    <span className="font-medium text-gray-700">YouTube</span>
                  </a>
                  <a href="#" className="flex items-center gap-2 bg-gray-100 hover:bg-gray-200 px-6 py-3 rounded-full transition-colors cursor-pointer whitespace-nowrap">
                    <i className="ri-spotify-line text-xl text-green-600"></i>
                    <span className="font-medium text-gray-700">Spotify</span>
                  </a>
                  <a href="#" className="flex items-center gap-2 bg-gray-100 hover:bg-gray-200 px-6 py-3 rounded-full transition-colors cursor-pointer whitespace-nowrap">
                    <i className="ri-apple-line text-xl text-gray-800"></i>
                    <span className="font-medium text-gray-700">Apple Podcasts</span>
                  </a>
                  <a href="#" className="flex items-center gap-2 bg-gray-100 hover:bg-gray-200 px-6 py-3 rounded-full transition-colors cursor-pointer whitespace-nowrap">
                    <i className="ri-rss-line text-xl text-orange-500"></i>
                    <span className="font-medium text-gray-700">RSS Feed</span>
                  </a>
                </div>
              </div>
            </div>
          )}

          {/* ===== ANIME TAB ===== */}
          {activeCategory === 'anime' && (
            <div>
              {/* アニメヒーローバナー */}
              <div className="relative rounded-2xl overflow-hidden mb-12 h-56">
                <img
                  src="https://readdy.ai/api/search-image?query=Anime%20style%20pharmacy%20world%20banner%2C%20multiple%20cute%20pharmacist%20characters%2C%20colorful%20medicine%20and%20health%20items%2C%20vibrant%20cheerful%20illustration%2C%20wide%20panoramic%20scene%2C%20pastel%20rainbow%20colors%2C%20educational%20and%20fun%20atmosphere&width=1200&height=400&seq=anime-hero-banner&orientation=landscape"
                  alt="薬局アニメサイト"
                  className="w-full h-full object-cover object-top"
                />
                <div className="absolute inset-0 bg-gradient-to-r from-amber-900/60 via-orange-800/30 to-transparent flex items-center px-10">
                  <div>
                    <span className="inline-block bg-amber-400 text-white text-xs font-bold px-3 py-1 rounded-full mb-3">NEW SERIES</span>
                    <h2 className="text-3xl font-bold text-white mb-2 drop-shadow">薬局アニメサイト</h2>
                    <p className="text-white/90 text-sm max-w-md drop-shadow">薬剤師の仕事・薬の知識をアニメで楽しく学ぼう。全8シリーズ配信中！</p>
                  </div>
                </div>
              </div>

              {/* フィルタータグ */}
              <div className="flex flex-wrap gap-2 mb-8 justify-center">
                {['すべて', '教育', '日常', '上級者向け'].map((tag) => (
                  <button
                    key={tag}
                    className="px-4 py-1.5 rounded-full text-sm font-medium border border-amber-300 text-amber-700 hover:bg-amber-500 hover:text-white hover:border-amber-500 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    {tag}
                  </button>
                ))}
              </div>

              {/* アニメカードグリッド */}
              <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
                {animeContent.map((anime, index) => (
                  <div
                    key={index}
                    className="bg-white rounded-xl overflow-hidden border border-amber-100 hover:border-amber-300 transition-all duration-300 group"
                  >
                    {/* サムネイル */}
                    <div className="relative overflow-hidden">
                      <img
                        src={anime.thumbnail}
                        alt={anime.title}
                        className="w-full h-44 object-cover object-top group-hover:scale-105 transition-transform duration-300"
                      />
                      <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors flex items-center justify-center">
                        <div className="w-12 h-12 bg-white/90 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                          <i className="ri-play-fill text-amber-500 text-xl ml-0.5"></i>
                        </div>
                      </div>
                      <div className="absolute top-2 left-2">
                        <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                          anime.category === 'educational' ? 'bg-amber-400 text-white' :
                          anime.category === 'advanced' ? 'bg-orange-500 text-white' :
                          'bg-teal-400 text-white'
                        }`}>
                          {anime.category === 'educational' ? '教育' : anime.category === 'advanced' ? '上級' : '日常'}
                        </span>
                      </div>
                      <div className="absolute bottom-2 right-2 bg-black/60 text-white text-xs px-2 py-0.5 rounded">
                        {anime.duration}
                      </div>
                    </div>

                    {/* カード本文 */}
                    <div className="p-4">
                      <h3 className="text-sm font-bold text-gray-900 mb-1.5 group-hover:text-amber-600 transition-colors leading-snug">
                        {anime.title}
                      </h3>
                      <p className="text-xs text-gray-500 leading-relaxed mb-3 line-clamp-2">
                        {anime.description}
                      </p>

                      {/* タグ */}
                      <div className="flex flex-wrap gap-1 mb-3">
                        {anime.tags.map((tag) => (
                          <span key={tag} className="text-xs bg-amber-50 text-amber-700 px-2 py-0.5 rounded-full border border-amber-200">
                            #{tag}
                          </span>
                        ))}
                      </div>

                      {/* 評価・視聴数 */}
                      <div className="flex items-center justify-between text-xs text-gray-500 mb-3">
                        <div className="flex items-center gap-1">
                          <i className="ri-star-fill text-amber-400"></i>
                          <span className="font-medium text-gray-700">{anime.rating}</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <i className="ri-eye-line"></i>
                          <span>{anime.views}回視聴</span>
                        </div>
                      </div>

                      <button
                        onClick={() => setSelectedAnime(anime)}
                        className="block w-full bg-amber-500 hover:bg-amber-600 text-white text-sm py-2 px-4 rounded-lg text-center transition-colors whitespace-nowrap cursor-pointer font-medium"
                      >
                        詳細を見る
                      </button>
                    </div>
                  </div>
                ))}
              </div>

              {/* もっと見るボタン */}
              <div className="text-center mt-10">
                <button className="inline-flex items-center gap-2 border-2 border-amber-400 text-amber-600 hover:bg-amber-500 hover:text-white px-8 py-3 rounded-full font-medium transition-colors cursor-pointer whitespace-nowrap">
                  <i className="ri-add-line"></i>
                  もっと見る
                </button>
              </div>
            </div>
          )}

          {/* ===== COMICS TAB ===== */}
          {activeCategory === 'comics' && (
            <div>
              {/* マンガヒーローバナー */}
              <div className="relative rounded-2xl overflow-hidden mb-10 h-52">
                <img
                  src="https://readdy.ai/api/search-image?query=Manga%20style%20pharmacy%20comic%20banner%2C%20multiple%20cute%20manga%20pharmacist%20characters%2C%20colorful%20comic%20book%20style%20illustration%2C%20wide%20panoramic%20scene%2C%20vibrant%20orange%20and%20amber%20tones%2C%20fun%20educational%20atmosphere%2C%20Japanese%20manga%20art&width=1200&height=380&seq=manga-hero-banner&orientation=landscape"
                  alt="薬局マンガ"
                  className="w-full h-full object-cover object-top"
                />
                <div className="absolute inset-0 bg-gradient-to-r from-orange-900/65 via-amber-800/30 to-transparent flex items-center px-10">
                  <div>
                    <span className="inline-block bg-orange-400 text-white text-xs font-bold px-3 py-1 rounded-full mb-3">全6シリーズ</span>
                    <h2 className="text-3xl font-bold text-white mb-2 drop-shadow">薬局マンガライブラリー</h2>
                    <p className="text-white/90 text-sm max-w-md drop-shadow">薬剤師の日常・歴史・キャリア・経営まで、マンガで楽しく学ぼう！</p>
                  </div>
                </div>
              </div>

              {/* 4コママンガセクション */}
              <div className="mb-12">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-1 h-6 bg-amber-500 rounded-full"></div>
                  <h3 className="text-xl font-bold text-gray-900">今週の4コマ</h3>
                  <span className="text-xs bg-red-100 text-red-600 px-2 py-0.5 rounded font-medium">NEW</span>
                </div>
                <FourPanelManga />
              </div>

              {/* マンガカードグリッド */}
              <div className="flex items-center gap-3 mb-6">
                <div className="w-1 h-6 bg-amber-500 rounded-full"></div>
                <h3 className="text-xl font-bold text-gray-900">マンガ一覧</h3>
                <span className="text-sm text-gray-400">全{comics.length}作品</span>
              </div>

              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {comics.map((comic, index) => (
                  <div
                    key={index}
                    className="bg-white rounded-xl overflow-hidden border border-amber-100 hover:border-amber-300 transition-all duration-300 group flex"
                  >
                    {/* 縦型サムネイル */}
                    <div className="relative w-32 flex-shrink-0 overflow-hidden">
                      <img
                        src={comic.thumbnail}
                        alt={comic.title}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-300"
                      />
                      <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors" />
                    </div>

                    {/* カード本文 */}
                    <div className="flex-1 p-4 flex flex-col justify-between min-w-0">
                      {/* ステータス・ジャンル */}
                      <div>
                        <div className="flex items-center gap-1.5 mb-2 flex-wrap">
                          <span className={`text-xs px-2 py-0.5 rounded-full font-medium border ${
                            comic.status === '連載中' ? 'bg-green-50 text-green-700 border-green-200' :
                            'bg-gray-50 text-gray-600 border-gray-200'
                          }`}>
                            {comic.status}
                          </span>
                          <span className="text-xs px-2 py-0.5 rounded-full font-medium bg-amber-50 text-amber-700 border border-amber-200">
                            {comic.genre}
                          </span>
                        </div>

                        <h3 className="text-sm font-bold text-gray-900 mb-1.5 group-hover:text-amber-600 transition-colors leading-snug">
                          {comic.title}
                        </h3>
                        <p className="text-xs text-gray-500 leading-relaxed mb-2 line-clamp-2">
                          {comic.description}
                        </p>

                        {/* タグ */}
                        <div className="flex flex-wrap gap-1 mb-2">
                          {comic.tags.slice(0, 2).map((tag) => (
                            <span key={tag} className="text-xs bg-amber-50 text-amber-600 px-1.5 py-0.5 rounded border border-amber-100">
                              #{tag}
                            </span>
                          ))}
                        </div>
                      </div>

                      {/* メタ情報 */}
                      <div>
                        <div className="flex items-center justify-between text-xs text-gray-400 mb-2">
                          <span className="flex items-center gap-1">
                            <i className="ri-star-fill text-amber-400"></i>
                            <span className="font-medium text-gray-600">{comic.rating}</span>
                          </span>
                          <span className="flex items-center gap-1">
                            <i className="ri-book-open-line"></i>
                            {comic.episodes}話
                          </span>
                          <span className="flex items-center gap-1">
                            <i className="ri-eye-line"></i>
                            {comic.reads}
                          </span>
                        </div>

                        <button
                          onClick={() => setSelectedManga(comic)}
                          className="w-full bg-amber-500 hover:bg-amber-600 text-white text-xs py-2 px-3 rounded-lg text-center transition-colors whitespace-nowrap cursor-pointer font-medium"
                        >
                          詳細を見る
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* もっと見るボタン */}
              <div className="text-center mt-10">
                <button className="inline-flex items-center gap-2 border-2 border-amber-400 text-amber-600 hover:bg-amber-500 hover:text-white px-8 py-3 rounded-full font-medium transition-colors cursor-pointer whitespace-nowrap">
                  <i className="ri-add-line"></i>
                  もっと見る
                </button>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-4 gap-8">
            <div>
              <div className="flex items-center mb-4">
                <div className="w-8 h-8 mr-2 relative">
                  <div className="absolute inset-0 bg-gradient-to-br from-teal-400 to-emerald-400 rounded-lg transform rotate-12"></div>
                  <div className="absolute inset-0 bg-gradient-to-br from-teal-300 to-emerald-300 rounded-lg flex items-center justify-center">
                    <i className="ri-heart-pulse-fill text-white text-sm"></i>
                  </div>
                </div>
                <div className="text-xl font-bold text-teal-400" style={{ fontFamily: '"Pacifico", serif' }}>
                  みんなの薬局～Pharmacare～
                </div>
              </div>
              <p className="text-gray-400">患者さんと薬剤師をつなぐ総合医療情報プラットフォーム</p>
            </div>
            <div>
              <h3 className="text-lg font-semibold mb-4">患者さん向け</h3>
              <ul className="space-y-2 text-gray-400">
                <li><Link to="/health-consultation" className="hover:text-white cursor-pointer">健康相談</Link></li>
                <li><Link to="/self-care" className="hover:text-white cursor-pointer">セルフケア</Link></li>
                <li><Link to="/kampo" className="hover:text-white cursor-pointer">漢方相談</Link></li>
              </ul>
            </div>
            <div>
              <h3 className="text-lg font-semibold mb-4">薬剤師向け</h3>
              <ul className="space-y-2 text-gray-400">
                <li><Link to="/sterile-preparation" className="hover:text-white cursor-pointer">無菌調剤</Link></li>
                <li><Link to="/home-care" className="hover:text-white cursor-pointer">在宅医療</Link></li>
                <li><Link to="/dx" className="hover:text-white cursor-pointer">薬局DX</Link></li>
                <li><Link to="/career" className="hover:text-white cursor-pointer">転職・派遣情報</Link></li>
              </ul>
            </div>
            <div>
              <h3 className="text-lg font-semibold mb-4">情報・リンク</h3>
              <ul className="space-y-2 text-gray-400">
                <li><Link to="/official-links" className="hover:text-white cursor-pointer">公的機関リンク</Link></li>
                <li><Link to="/entertainment" className="hover:text-white cursor-pointer">エンタメ</Link></li>
                <li><Link to="/contact" className="hover:text-white cursor-pointer">お問い合わせ</Link></li>
                <li><Link to="/privacy-policy" className="hover:text-white cursor-pointer">プライバシーポリシー</Link></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-gray-800 mt-8 pt-8 text-center text-gray-400">
            <p>&copy; 2024 みんなの薬局～Pharmacare～. All rights reserved. | <a href="https://readdy.ai/?origin=logo" className="hover:text-white cursor-pointer">Website Builder</a></p>
          </div>
        </div>
      </footer>
    </div>
  );
}
